import { Fragment, type CSSProperties } from 'react'
import { Check, Plus, X } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { formatPct, formatRevenueCompact, formatRevenueFull } from './formatters'
import { GrowthInput, TargetInput } from './NodeCellInputs'
import type { PlanNode } from './PlanNodesPanel'

export function effectiveTarget(node: PlanNode): number {
  return node.targetRevenue ?? Number(node.revenue) ?? 0
}

export default function NodeCells({
  node,
  depth,
  removable,
  readOnly,
  newShare,
  currency,
  fillableRest,
  deductibleRest,
  highlighted,
  revision,
  onDrill,
  onRemove,
  onTargetChange
}: {
  node: PlanNode
  depth: number
  removable: boolean
  readOnly: boolean
  newShare: number
  currency?: string | null
  fillableRest: number
  deductibleRest: number
  // A row has open revision-request comments under it — tint the cells.
  highlighted?: boolean
  // Set only for a quarterly revision: an actuals cell (months already sold)
  // after Past Contribution, the full year under the target cell, and growth
  // read on the full year.
  revision?: { quarters: string; actualQuarters: string } | null
  onDrill: (n: PlanNode) => void
  onRemove: (id: string) => void
  onTargetChange: (id: string, target: number | null, growthPctOverride?: number | null) => void
}) {
  const canDrill = !!node.productNodeHasChildren && !node.special && !node.isNewProduct
  const cellStyle: CSSProperties = { borderColor: 'var(--border)' }
  const cellClass = highlighted ? ' bg-primary/5' : ''
  const baseRevenue = Number(node.revenue) || 0
  const actualRevenue = Number(node.actualRevenue ?? 0)
  const targetValue = node.targetRevenue ?? baseRevenue
  const isParent = node.children.length > 0
  const childrenSum = isParent
    ? node.children.reduce((sum, c) => sum + effectiveTarget(c), 0)
    : 0
  const isBalanced = isParent && Math.abs(childrenSum - effectiveTarget(node)) < 1

  return (
    <Fragment>
      <div
        className={`border-b py-2 min-w-0 flex items-center gap-1${cellClass}`}
        style={{ ...cellStyle, paddingLeft: 16 + depth * 20, paddingRight: 12 }}
      >
        {canDrill ? (
          <button
            type="button"
            onClick={() => onDrill(node)}
            title="Add child nodes"
            aria-label="Add child nodes"
            className="shrink-0 inline-flex items-center justify-center rounded transition-colors hover:bg-accent"
            style={{ width: 18, height: 18, color: 'var(--muted-foreground)' }}
          >
            <Plus size={14} strokeWidth={2} />
          </button>
        ) : (
          <div style={{ width: 18 }} aria-hidden />
        )}
        {canDrill ? (
          <button
            type="button"
            onClick={() => onDrill(node)}
            className="text-sm truncate text-left hover:underline"
            style={{
              color: 'var(--foreground)',
              fontWeight: depth === 0 ? 500 : 400,
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer'
            }}
          >
            {node.shortName || node.name}
          </button>
        ) : (
          <div
            className="text-sm truncate"
            style={{ color: 'var(--foreground)', fontWeight: depth === 0 ? 500 : 400 }}
          >
            {node.shortName || node.name}
          </div>
        )}
        {isParent && !isBalanced ? (
          <X
            size={14}
            strokeWidth={2.5}
            className="shrink-0"
            style={{ color: 'var(--destructive, #c53030)', marginLeft: 4 }}
            aria-label="Not balanced"
          />
        ) : (
          <Check
            size={14}
            strokeWidth={2.5}
            className="shrink-0"
            style={{ color: 'var(--success, #16a34a)', marginLeft: 4 }}
            aria-label="Balanced"
          />
        )}
      </div>
      <div
        className={`border-b py-2 px-3 flex flex-col items-end justify-center${cellClass}`}
        style={cellStyle}
      >
        <div className="text-sm tabular-nums" style={{ color: 'var(--foreground)' }}>
          {formatPct(node.share)}
        </div>
        <div
          className="text-xs tabular-nums"
          style={{ color: 'var(--muted-foreground)' }}
          title={formatRevenueFull(node.revenue, currency)}
        >
          {formatRevenueCompact(node.revenue, currency)}
        </div>
      </div>
      {revision && (
        <div className={`border-b py-2 px-3 flex items-center justify-end${cellClass}`} style={cellStyle}>
          <span
            className="text-sm tabular-nums"
            style={{ color: 'var(--foreground)' }}
            title={formatRevenueFull(actualRevenue, currency)}
          >
            {formatRevenueCompact(actualRevenue, currency)}
          </span>
        </div>
      )}
      <GrowthInput
        node={node}
        baseRevenue={baseRevenue}
        actualRevenue={actualRevenue}
        readOnly={readOnly}
        cellClass={cellClass}
        cellStyle={cellStyle}
        onTargetChange={onTargetChange}
      />
      <TargetInput
        node={node}
        baseRevenue={baseRevenue}
        targetValue={targetValue}
        fullYear={revision ? actualRevenue + targetValue : null}
        currency={currency}
        readOnly={readOnly}
        fillableRest={fillableRest}
        deductibleRest={deductibleRest}
        cellClass={cellClass}
        cellStyle={cellStyle}
        onTargetChange={onTargetChange}
      />
      <div
        className={`border-b py-2 px-3 flex items-center justify-end${cellClass}`}
        style={cellStyle}
      >
        <span className="text-sm tabular-nums" style={{ color: 'var(--foreground)' }}>
          {formatPct(newShare)}
        </span>
      </div>
      <div
        className={`border-b py-2 flex items-center justify-center${cellClass}`}
        style={cellStyle}
      >
        {!readOnly && removable && (
          <Button
            variant="outline"
            size="sm"
            className="h-7 px-2.5 text-xs"
            onClick={() => onRemove(node.id)}
          >
            Remove
          </Button>
        )}
      </div>
    </Fragment>
  )
}
